import { createContext, useEffect, useMemo, useState } from "react"
import { permisionURL } from "../js/Domain"

/** 顏色設定檔type (同 json 檔應有顏色系) */
export type ThemeSetContextType = {
    /** 最常訪問頁面 IDB 資料 */
    themeColor: {
        primary: string,
        primary_hover: string,
        secondary: string,
        secondary_hover: string,
        secondary_checked: string,
        info: string,
        success: string,
        warning: string,
        error: string,
        error_dark: string,
        primary_transparent: string,
        secondary_transparent: string,
        background: string,
        seocond_background: string,
        primary_icon_btn: string,
        primary_table: string,
        secondary_table: string,
        dark300: string,
        dark200: string,
        dark100: string,
        light200: string,
        light100: string,
        option_selected: string,
        option_hover: string,
        disabled: string,
        info_alert_color: string,
        info_alert_background: string,
        warning_alert_color: string,
        warning_alert_background: string,
        error_alert_color: string,
        error_alert_background: string,
        presentHoverColor: string,
        absentHoverColor: string,
        leaveHoverColor: string,
    },
}

/** 顏色設定檔 Context */
export const ThemeSetContext = createContext<ThemeSetContextType>({} as ThemeSetContextType)

/** 顏色設定檔 Context Provider */
export const ThemeSetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    /** 公司代碼 */
    let company_code: string | null = sessionStorage.getItem("company_code");

    // 顏色設定檔
    const [themeColor, setThemeColor] = useState<ThemeSetContextType['themeColor']>({
        primary: "#5b6fac",
        primary_hover: "#7f92cc",
        secondary: "#4db6ac",
        secondary_hover: "#80cbc4",
        secondary_checked: "#80cbc4",
        info: "#7986cb",
        success: "#4db6ac",
        warning: "#ff9100",
        error: "#ef5350",
        error_dark: "#f44336",
        primary_transparent: "#0000000c",
        secondary_transparent: "#4db6ac0f",
        background: "#f5f7f9",
        seocond_background: "#efeff1",
        primary_icon_btn: "#727b97",
        primary_table: "#67749e",
        secondary_table: "#999999",
        dark300: "#293d78",
        dark200: "#495887",
        dark100: "#d9d9d9",
        light200: "#2196f3",
        light100: "#e7f2ff",
        option_selected: "#e7f2ff",
        option_hover: "#8f9dc4",
        disabled: "#babababa",
        info_alert_color: "#0d3c61",
        info_alert_background: "#e8f4fd",
        warning_alert_color: "#663c00",
        warning_alert_background: "#fff4e5",
        error_alert_color: "#611a15",
        error_alert_background: "#fdecea",
        presentHoverColor: "#92EA9B",
        absentHoverColor: "#F67564",
        leaveHoverColor: "#EADD92",
    })

    /** 查詢 主題顏色資料 */
    async function searchThemeSetData() {
        try {
            // 取得主題顏色資料 json 檔
            const response = await fetch(`${permisionURL}/_${company_code}/systemTheme.json`);
            const jsonData = await response.json();
            setThemeColor(prev => ({
                primary: jsonData.themeColor?.primary ?? prev.primary,
                primary_hover: jsonData.themeColor?.primary_hover ?? prev.primary_hover,
                secondary: jsonData.themeColor?.secondary ?? prev.secondary,
                secondary_hover: jsonData.themeColor?.secondary_hover ?? prev.secondary_hover,
                secondary_checked: jsonData.themeColor?.secondary_checked ?? prev.secondary_checked,
                info: jsonData.themeColor?.info ?? prev.info,
                success: jsonData.themeColor?.success ?? prev.success,
                warning: jsonData.themeColor?.warning ?? prev.warning,
                error: jsonData.themeColor?.error ?? prev.error,
                error_dark: jsonData.themeColor?.error_dark ?? prev.error_dark,
                primary_transparent: jsonData.themeColor?.primary_transparent ?? prev.primary_transparent,
                secondary_transparent: jsonData.themeColor?.secondary_transparent ?? prev.secondary_transparent,
                background: jsonData.themeColor?.background ?? prev.background,
                seocond_background: jsonData.themeColor?.seocond_background ?? prev.seocond_background,
                primary_icon_btn: jsonData.themeColor?.primary_icon_btn ?? prev.primary_icon_btn,
                primary_table: jsonData.themeColor?.primary_table ?? prev.primary_table,
                secondary_table: jsonData.themeColor?.secondary_table ?? prev.secondary_table,
                dark300: jsonData.themeColor?.dark300 ?? prev.dark300,
                dark200: jsonData.themeColor?.dark200 ?? prev.dark200,
                dark100: jsonData.themeColor?.dark100 ?? prev.dark100,
                light200: jsonData.themeColor?.light200 ?? prev.light200,
                light100: jsonData.themeColor?.light100 ?? prev.light100,
                option_selected: jsonData.themeColor?.option_selected ?? prev.option_selected,
                option_hover: jsonData.themeColor?.option_hover ?? prev.option_hover,
                disabled: jsonData.themeColor?.disabled ?? prev.disabled,
                info_alert_color: jsonData.themeColor?.info_alert_color ?? prev.info_alert_color,
                info_alert_background: jsonData.themeColor?.info_alert_background ?? prev.info_alert_background,
                warning_alert_color: jsonData.themeColor?.warning_alert_color ?? prev.warning_alert_color,
                warning_alert_background: jsonData.themeColor?.warning_alert_background ?? prev.warning_alert_background,
                error_alert_color: jsonData.themeColor?.error_alert_color ?? prev.error_alert_color,
                error_alert_background: jsonData.themeColor?.error_alert_background ?? prev.error_alert_background,
                presentHoverColor: jsonData.themeColor?.presentHoverColor ?? prev.presentHoverColor,
                absentHoverColor: jsonData.themeColor?.absentHoverColor ?? prev.absentHoverColor,
                leaveHoverColor: jsonData.themeColor?.leaveHoverColor ?? prev.leaveHoverColor,
            }))
        } catch (error) {
            console.error("Failed to fetch theme set:", error);
        }
    }

    useEffect(() => {
        searchThemeSetData()
    }, [])

    const value = useMemo(() => ({ themeColor }), [themeColor]);

    return (
        <ThemeSetContext.Provider value={value}>
            {children}
        </ThemeSetContext.Provider>
    )
}